import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getPasswordPolicyInfo } from '../../services/passwordPolicy';
import { logger } from '../../lib/logger';

export default function PasswordExpiryBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [imposedByAdmin, setImposedByAdmin] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let mounted = true;

    const check = async () => {
      try {
        // Vérifier si un renouvellement a été imposé par un admin
        const { data: userData } = await supabase
          .from('users')
          .select('must_change_password')
          .eq('id', user.id)
          .maybeSingle();

        if (!mounted) return;

        if (userData?.must_change_password) {
          setImposedByAdmin(true);
          setIsExpired(false);
          setDaysLeft(null);
          return;
        }

        const info = await getPasswordPolicyInfo(user.id);
        if (!info || !mounted) return;

        if (info.isExpired) {
          setIsExpired(true);
          setDaysLeft(0);
          setImposedByAdmin(false);
        } else if (info.showWarning) {
          setDaysLeft(info.daysUntilExpiry);
          setIsExpired(false);
          setImposedByAdmin(false);
        } else {
          setDaysLeft(null);
          setIsExpired(false);
          setImposedByAdmin(false);
        }
      } catch (error) {
        logger.error('Failed to fetch password policy info:', error);
        if (mounted) {
          setDaysLeft(null);
          setIsExpired(false);
          setImposedByAdmin(false);
        }
      }
    };

    check();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  if (dismissed || (!imposedByAdmin && daysLeft === null && !isExpired)) return null;

  const isUrgent = isExpired || (daysLeft !== null && daysLeft <= 3);

  // Bannière spéciale : renouvellement imposé par un admin
  if (imposedByAdmin) {
    return (
      <div className="px-4 py-3 flex items-center justify-between gap-4 bg-red-50 border-b border-red-200">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-red-100">
            <i className="ri-admin-line text-lg text-red-600"></i>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-red-900 flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded uppercase tracking-wide">
                Action requise
              </span>
              Un administrateur a imposé le renouvellement de votre mot de passe
            </p>
            <p className="text-xs mt-0.5 text-red-700">
              Pour des raisons de sécurité, vous devez définir un nouveau mot de passe immédiatement. Cette action est obligatoire.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/profil')}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap cursor-pointer transition-colors bg-red-600 text-white hover:bg-red-700"
          >
            <i className="ri-lock-password-line mr-1"></i>
            Changer maintenant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`px-4 py-3 flex items-center justify-between gap-4 ${
        isExpired
          ? 'bg-red-50 border-b border-red-200'
          : isUrgent
          ? 'bg-amber-50 border-b border-amber-200'
          : 'bg-amber-50/70 border-b border-amber-100'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isExpired ? 'bg-red-100' : 'bg-amber-100'
          }`}
        >
          <i
            className={`text-lg ${
              isExpired
                ? 'ri-error-warning-fill text-red-600'
                : 'ri-alarm-warning-line text-amber-600'
            }`}
          ></i>
        </div>
        <div className="min-w-0">
          <p
            className={`text-sm font-semibold ${
              isExpired ? 'text-red-900' : 'text-amber-900'
            }`}
          >
            {isExpired
              ? 'Votre mot de passe a expiré'
              : `Votre mot de passe expire dans ${daysLeft} jour${
                  daysLeft !== null && daysLeft > 1 ? 's' : ''
                }`}
          </p>
          <p
            className={`text-xs mt-0.5 ${
              isExpired ? 'text-red-700' : 'text-amber-700'
            }`}
          >
            {isExpired
              ? 'Vous devez le changer immédiatement pour continuer à utiliser la plateforme.'
              : 'Pensez à le renouveler pour maintenir la sécurité de votre compte.'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => navigate('/profil')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap cursor-pointer transition-colors ${
            isExpired
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-amber-600 text-white hover:bg-amber-700'
          }`}
        >
          <i className="ri-lock-password-line mr-1"></i>
          Changer maintenant
        </button>
        {!isExpired && (
          <button
            onClick={() => setDismissed(true)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-amber-500 hover:bg-amber-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        )}
      </div>
    </div>
  );
}
